<template>
    <div class="premedicinering-list">
        <admin-lock-border />
        <div class="header">
            <h1>Premedicinering</h1>
            <KButton type="button" variant="hollow" @click="openCreatePremedicinering">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa premedicinering</span>
            </KButton>
        </div>

        <div class="newly" v-if="redigeradePremedicinering.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen redigerade premedicineringar">
                <kliniken-table
                    :items="redigeradePremedicinering"
                    :columns="columns"
                    @editPremedicinering="editPremedicineringFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div class="newly-created" v-if="nyaPremedicinering.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen tillagda premedicineringar">
                <kliniken-table
                    :items="nyaPremedicinering"
                    :columns="columns"
                    @editPremedicinering="editPremedicineringFromTable"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>

        <div>
            <h3 v-if="nyaPremedicinering.length > 0 || redigeradePremedicinering.length > 0">Alla premedicineringar</h3>
            <kliniken-table
                :items="premedicinering"
                paginationStyle="paging"
                :columns="columns"
                :count="listCount"
                :limit="listLimit"
                :currentPage="listPage"
                @loadPage="onLoadPage"
                @changeLimit="onChangeLimit"
                @sort="onSortChange"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
                @editPremedicinering="editPremedicineringFromTable"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "PremedicineringList",
        tabName: "Premedicinering",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import KButton from "@/components/Button.vue";
    import PremedicineringForm from "./PremedicineringForm.vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";

    interface Premedicinering {
        id: number;
        artikelnummer: string;
        anestesi: unknown;
        lakemedelsartikel: unknown;
        ordning: number;
        namn: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({ tabId });

    const listPage = ref<number>(1);
    const listLimit = ref<number>(20);
    const filterParams = ref<{ [key: string]: string } | undefined>();
    const subsetName = ref<string>("default");

    const listCount = computed(() => store.state.operation.premedicinering.subsetTotals?.[subsetName.value] ?? 0);

    const premedicinering = computed<Premedicinering[]>(() =>
        store.getters["operation/premedicinering/premedicineringSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const nyaPremedicinering = computed<Premedicinering[]>(() =>
        store.getters["operation/premedicinering/premedicineringSubset"]({
            subsetName: "newlyCreated",
            offset: 0,
            limit: listLimit.value,
        })
    );

    const redigeradePremedicinering = computed<Premedicinering[]>(() =>
        store.getters["operation/premedicinering/premedicineringSubset"]({
            subsetName: "newlyUpdated",
            offset: 0,
            limit: listLimit.value,
        })
    );

    const columns = [
        {
            title: "Namn",
            name: "namn",
            sort: "namn",
        },
        {
            title: "Ordning",
            name: "ordning",
            sort: "ordning",
        },
        {
            title: "Anestesi",
            name: "anestesi_display.displayName",
        },
    ];

    const dropdownActions = [
        {
            name: "editPremedicinering",
            title: "Redigera premedicinering",
            show: () => true,
        },
    ];

    const onLoadPage = async (page: number) => {
        await fetchPremedicinering({ page });
    };

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;
        await fetchPremedicinering({ page: 1, limit: newLimit });
    };

    const onSortChange = async (newSortBy: string) => {
        filterParams.value = {
            ...(filterParams.value ?? {}),
            ordering: newSortBy,
        };
        await fetchPremedicinering({ page: 1 });
    };

    const fetchPremedicinering = async ({
        page,
        limit = listLimit.value,
        forceRefresh = false,
    }: {
        page: number;
        limit?: number;
        forceRefresh?: boolean;
    }) => {
        try {
            const { subsetName: fetchedSubsetName } = await store.dispatch(
                "operation/premedicinering/fetchPremedicinering",
                {
                    page,
                    limit,
                    forceRefresh,
                    filterParams: filterParams.value,
                }
            );

            listPage.value = page;
            subsetName.value = fetchedSubsetName;
        } catch (_) {
            console.log(_);
            openDialog("Kunde inte hämta premedicinering", "error");
        }
    };

    const openCreatePremedicinering = () => {
        store.dispatch("tabs/openTab", {
            component: PremedicineringForm,
        });
    };

    const editPremedicineringFromTable = (premedicinering: Premedicinering) => {
        store.dispatch("tabs/openTab", {
            component: PremedicineringForm,
            parameters: {
                premedicineringId: premedicinering.id,
            },
        });
    };

    const onRefreshTab = async () => {
        await fetchPremedicinering({ page: 1, forceRefresh: true });
    };

    onBeforeMount(async () => {
        await fetchPremedicinering({ page: listPage.value });
        await store.dispatch("operation/premedicinering/addSubscriber");
        emitter.on(`refreshTab-${tabId.value}`, onRefreshTab);
    });

    onBeforeUnmount(async () => {
        await store.dispatch("operation/premedicinering/removeSubscriber");
        emitter.off(`refreshTab-${tabId.value}`, onRefreshTab);
    });
</script>

<style lang="scss">
    .premedicinering-list {
        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }

        .newly {
            margin-top: 1rem;
        }
    }
</style>
